import React, { useMemo } from 'react';
import { Card, Select, Empty } from 'antd';
import { Line } from '@ant-design/charts';
import type { TrendPoint, TrendFilters, ModuleListItem } from '../../../services/devApi';

interface TestTrendChartProps {
  trendData: TrendPoint[];
  loading: boolean;
  filters: TrendFilters;
  modules: ModuleListItem[];
  onFilterChange: (filters: Partial<TrendFilters>) => void;
}

const CATEGORY_OPTIONS = [
  { value: '', label: '全部分類' },
  { value: 'validator', label: 'Validator' },
  { value: 'service', label: 'Service' },
  { value: 'integration', label: 'Integration' },
  { value: 'e2e', label: 'E2E' },
];

const LIMIT_OPTIONS = [
  { value: 10, label: '最近 10 筆' },
  { value: 20, label: '最近 20 筆' },
  { value: 50, label: '最近 50 筆' },
];

const CATEGORY_COLORS: Record<string, string> = {
  validator: '#1677ff',
  service: '#13c2c2',
  integration: '#722ed1',
  e2e: '#2f54eb',
};

function formatRunAt(dateStr: string): string {
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${min}`;
}

const TestTrendChart: React.FC<TestTrendChartProps> = ({
  trendData,
  loading,
  filters,
  modules,
  onFilterChange,
}) => {
  const moduleOptions = useMemo(() => {
    const opts = [{ value: '', label: '全部模組' }];
    const seen = new Set<string>();
    modules.forEach((m) => {
      if (!seen.has(m.formId)) {
        seen.add(m.formId);
        opts.push({ value: m.formId, label: m.name || m.formId });
      }
    });
    return opts;
  }, [modules]);

  const chartData = useMemo(() => {
    if (!trendData || trendData.length === 0) return [];
    return trendData
      .sort((a, b) => new Date(a.runAt).getTime() - new Date(b.runAt).getTime())
      .map((p) => ({
        ...p,
        runAtLabel: formatRunAt(p.runAt),
        durationSec: Number((p.totalDurationMs / 1000).toFixed(2)),
      }));
  }, [trendData]);

  const lineConfig = useMemo(() => ({
    data: chartData,
    xField: 'runAtLabel',
    yField: 'durationSec',
    colorField: 'category',
    color: (datum: { category?: string }) => CATEGORY_COLORS[datum.category || ''] || '#8c8c8c',
    point: { size: 4, shape: 'circle' },
    interaction: { tooltip: { marker: true } },
    axis: {
      x: { title: '執行時間', labelAutoRotate: true },
      y: { title: '耗時 (秒)' },
    },
    legend: { color: { position: 'top-right' as const } },
    height: 300,
  }), [chartData]);

  return (
    <Card title="測試效能趨勢" loading={loading}>
      <div className="flex items-center gap-4 mb-4">
        <Select
          placeholder="全部模組"
          allowClear
          style={{ width: 200 }}
          value={filters.formId || undefined}
          onChange={(val) => onFilterChange({ formId: val || undefined })}
          options={moduleOptions}
        />
        <Select
          placeholder="全部分類"
          allowClear
          style={{ width: 150 }}
          value={filters.category || undefined}
          onChange={(val) => onFilterChange({ category: val || undefined })}
          options={CATEGORY_OPTIONS}
        />
        <Select
          style={{ width: 130 }}
          value={filters.limit || 20}
          onChange={(val) => onFilterChange({ limit: val })}
          options={LIMIT_OPTIONS}
        />
      </div>
      {chartData.length === 0 ? (
        <Empty description="尚無趨勢資料" />
      ) : (
        <Line {...lineConfig} />
      )}
    </Card>
  );
};

export default TestTrendChart;
